import React, { useMemo, useState } from "react";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { Button } from "../../../../ui/components/Button";
import { Badge } from "../../../../ui/components/Badge";
import { IconButton } from "../../../../ui/components/IconButton";
import { Table } from "../../../../ui/components/Table";
import { TextField } from "../../../../ui/components/TextField";
import { Alert } from "../../../../ui/components/Alert";
import { Tabs } from "../../../../ui/components/Tabs";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import {
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherExternalLink,
  FeatherRefreshCw,
  FeatherSearch,
} from "@subframe/core";
import { useAgentReviewList } from "../hooks/useAgentReview";
import {
  AgentReviewListItem,
  AgentReviewStatus,
  STATUS_LABEL,
} from "../../domain/entities/AgentReviewEntities";
import AgentReviewStatusBadge from "../components/AgentReviewStatusBadge";
import AgentReviewDrawer from "../components/AgentReviewDrawer";
import AgentReviewDashboard from "../components/AgentReviewDashboard";

/**
 * ZOUR-67 — Agent Review.
 *
 * The queue of uploaded bills that have not been posted yet, each with what a
 * second accountant made of it. The page answers one question — which of these
 * can I post without reading them — and gets out of the way for the rest.
 *
 * It never edits or posts a bill. Every row leads to `/bills/review`, which
 * owns both.
 */

const STATUS_ORDER: AgentReviewStatus[] = [
  "needs_attention",
  "ready",
  "checking",
  "not_checked",
];

interface Props {
  /**
   * Whether this user may press Re-check and mark findings "Not an error".
   * The backend gates both on `bill.edit` regardless; this only decides whether
   * the buttons are worth showing.
   */
  canEdit?: boolean;
}

export const AgentReviewPage: React.FC<Props> = ({ canEdit = true }) => {
  const { items, counts, pagination, filters, updateFilters, loading, error, available, reload } =
    useAgentReviewList();

  const [tab, setTab] = useState<"queue" | "dashboard">("queue");
  // Off by default: the queue is a list of work, and a posted bill is finished
  // work. Opt in to read what the agent made of bills as they arrived.
  const [includePosted, setIncludePosted] = useState(false);
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState<AgentReviewListItem | null>(null);

  const activeStatus = (filters.status ?? "") as AgentReviewStatus | "";

  // The whole distribution, not just what the status filter left — the pills
  // are how you move between statuses, so they each have to keep their number.
  const total = useMemo(
    () => STATUS_ORDER.reduce((sum, s) => sum + (counts[s] ?? 0), 0),
    [counts],
  );

  // Enter anywhere in the toolbar commits everything in it at once.
  const submitFilters = (event: React.FormEvent) => {
    event.preventDefault();
    updateFilters({ search: search.trim(), from, to });
  };

  const money = (value: string | null) => {
    if (value === null || value === "") return "—";
    const n = Number(value);
    return Number.isFinite(n) ? `₹${n.toLocaleString("en-IN")}` : value;
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-6 py-6 overflow-auto">
        {/* Heading */}
        <div className="flex w-full flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Agent Review
            </span>
            <span className="text-body font-body text-subtext-color">
              Every uploaded bill that is not posted yet, re-read against its own
              document. Nothing here posts anything.
            </span>
          </div>
          {tab === "queue" && (
            <Button
              variant="neutral-secondary"
              icon={<FeatherRefreshCw />}
              loading={loading}
              onClick={reload}
            >
              Refresh
            </Button>
          )}
        </div>

        {/* One page, two questions: which bills need me now (Queue), and is
            this thing worth reading at all (Dashboard). Keeping them together
            means the numbers sit next to the work they describe, and the nav
            footprint stays at one route. */}
        <Tabs>
          <Tabs.Item active={tab === "queue"} onClick={() => setTab("queue")}>
            Queue
          </Tabs.Item>
          <Tabs.Item
            active={tab === "dashboard"}
            onClick={() => setTab("dashboard")}
          >
            Dashboard
          </Tabs.Item>
        </Tabs>

        {!available && (
          <Alert
            variant="neutral"
            title="Not switched on for this client yet"
            description="Agent Review needs its database tables, which are created by the migration run after a release. Nothing is broken; there is simply nothing to show yet."
          />
        )}

        {available && tab === "dashboard" && <AgentReviewDashboard />}

        {tab === "queue" && error && (
          <Alert variant="error" title="Could not load" description={error} />
        )}

        {/* Status filter */}
        {available && tab === "queue" && (
          <div className="flex w-full flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => updateFilters({ status: "" })}
              className={`rounded-md border border-solid px-3 py-1.5 ${
                activeStatus === ""
                  ? "border-brand-600 bg-brand-50"
                  : "border-neutral-border bg-default-background"
              }`}
            >
              <span className="text-body font-body text-default-font">
                All {total > 0 ? `(${total})` : ""}
              </span>
            </button>
            {STATUS_ORDER.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => updateFilters({ status })}
                className={`rounded-md border border-solid px-3 py-1.5 ${
                  activeStatus === status
                    ? "border-brand-600 bg-brand-50"
                    : "border-neutral-border bg-default-background"
                }`}
              >
                <span className="text-body font-body text-default-font">
                  {STATUS_LABEL[status]} ({counts[status] ?? 0})
                </span>
              </button>
            ))}

            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-1.5">
              <input
                type="checkbox"
                checked={includePosted}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setIncludePosted(e.target.checked);
                  updateFilters({ include_posted: e.target.checked ? 1 : "" });
                }}
              />
              <span className="whitespace-nowrap text-body font-body text-default-font">
                Include posted bills
              </span>
            </label>

            <form onSubmit={submitFilters} className="ml-auto flex items-center gap-2">
              <TextField label="" helpText="" icon={<FeatherSearch />}>
                <TextField.Input
                  placeholder="Bill number or vendor"
                  value={search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearch(e.target.value)
                  }
                />
              </TextField>
              {/* Held locally and committed on blur or Enter. A native date
                  input fires onChange for every partial date you type, and each
                  one of those would be a request for a date nobody meant. */}
              <TextField label="" helpText="">
                <TextField.Input
                  type="date"
                  value={from}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFrom(e.target.value)
                  }
                  onBlur={() => updateFilters({ from })}
                />
              </TextField>
              <TextField label="" helpText="">
                <TextField.Input
                  type="date"
                  value={to}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setTo(e.target.value)
                  }
                  onBlur={() => updateFilters({ to })}
                />
              </TextField>
            </form>
          </div>
        )}

        {/* The queue */}
        {available && tab === "queue" && (
          <div className="flex w-full flex-col gap-3">
            {/* If the columns still cannot all fit, the TABLE scrolls, not the
                page. A page-level overflow pushes the trailing action out of
                the viewport with no way to reach it. */}
            <div className="w-full overflow-x-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Bill</Table.HeaderCell>
                  <Table.HeaderCell>Vendor</Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Amount</Table.HeaderCell>
                  <Table.HeaderCell>Findings</Table.HeaderCell>
                  <Table.HeaderCell>What the agent said</Table.HeaderCell>
                  <Table.HeaderCell>{""}</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {loading && items.length === 0 ? (
                <TableSkeletonRows rowCount={6} columnCount={8} />
              ) : items.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={8}>
                    <span className="text-body font-body text-subtext-color">
                      No bills match these filters.
                    </span>
                  </Table.Cell>
                </Table.Row>
              ) : (
                items.map((item) => (
                  <Table.Row
                    key={item.bill_id}
                    clickable={true}
                    onClick={() => setSelected(item)}
                  >
                    <Table.Cell>
                      <AgentReviewStatusBadge
                        status={item.status}
                        stale={item.stale}
                      />
                    </Table.Cell>
                    {/* Capped, not just nowrap. An unbounded nowrap cell is what
                        let a bill number like
                        INV/AKR/CH/Professional and Expense/2025-26/17 take 354px
                        and squeeze the summary to three words. The full value is
                        on hover. */}
                    <Table.Cell className="max-w-[12rem]">
                      <div className="flex w-full flex-col gap-0.5">
                        <span
                          className="block truncate text-body-bold font-body-bold text-default-font"
                          title={item.bill_number || `#${item.bill_id}`}
                        >
                          {item.bill_number || `#${item.bill_id}`}
                        </span>
                        {item.is_posted && (
                          <span
                            className="w-fit rounded-sm bg-neutral-100 px-1 text-caption font-caption text-subtext-color"
                            title="Already in Zoho. The status is what the agent made of it as it arrived."
                          >
                            Posted
                          </span>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="max-w-[12rem]">
                      <span
                        className="block truncate text-body font-body text-default-font"
                        title={item.vendor_name || ""}
                      >
                        {item.vendor_name || "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-subtext-color">
                        {item.date || "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-body font-body text-default-font">
                        {money(item.total)}
                      </span>
                    </Table.Cell>
                    {/* A stale row's findings and summary describe a version of
                        the bill that no longer exists. They still tell you
                        something, so they stay — dimmed and labelled, so nobody
                        reads them as current. */}
                    <Table.Cell>
                      <div
                        className={`flex items-center gap-1 ${item.stale ? "opacity-40" : ""}`}
                      >
                        {item.mismatch_count > 0 && (
                          <Badge variant="error">{item.mismatch_count}</Badge>
                        )}
                        {item.advisory_count > 0 && (
                          <Badge variant="neutral">{item.advisory_count}</Badge>
                        )}
                        {item.mismatch_count === 0 && item.advisory_count === 0 && (
                          <span className="text-body font-body text-subtext-color">
                            —
                          </span>
                        )}
                      </div>
                    </Table.Cell>
                    {/* The column the page exists for, so it gets a floor rather
                        than whatever the other columns leave over. Two clamped
                        lines at this width are a readable sentence; the whole
                        thing is on hover. */}
                    <Table.Cell className="min-w-[22rem]">
                      <span
                        className={`line-clamp-2 text-body font-body text-subtext-color ${
                          item.stale ? "italic opacity-50" : ""
                        }`}
                        title={
                          item.stale && (item.summary || item.error)
                            ? `From an earlier check — ${item.error || item.summary}`
                            : item.error || item.summary || ""
                        }
                      >
                        {item.stale && (item.summary || item.error)
                          ? `From an earlier check — ${item.error || item.summary}`
                          : item.error || item.summary || "—"}
                      </span>
                    </Table.Cell>
                    {/* A real anchor, not a button calling `window.open`.
                        `window.open` can be refused by a popup blocker even on
                        a trusted click, and a link costs nothing extra: it
                        gives cmd-click, middle-click and the URL on hover for
                        free. `stopPropagation` keeps the row's own click — the
                        drawer — from firing as well, because triaging a queue
                        means opening several bills side by side. */}
                    <Table.Cell>
                      <a
                        href={`/bills/review?documentId=${item.bill_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open this bill in review, in a new tab"
                        aria-label="Open this bill in review, in a new tab"
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                          e.stopPropagation()
                        }
                        className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-transparent text-default-font hover:bg-neutral-100 active:bg-neutral-200"
                      >
                        <FeatherExternalLink className="text-body font-body" />
                      </a>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table>
            </div>

            {pagination.pages > 1 && (
              <div className="flex w-full items-center justify-end gap-2">
                <span className="text-caption font-caption text-subtext-color">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} bills
                </span>
                <IconButton
                  icon={<FeatherChevronLeft />}
                  disabled={pagination.page <= 1}
                  onClick={() => updateFilters({ page: pagination.page - 1 })}
                />
                <IconButton
                  icon={<FeatherChevronRight />}
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => updateFilters({ page: pagination.page + 1 })}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <AgentReviewDrawer
        bill={selected}
        canEdit={canEdit}
        onClose={() => setSelected(null)}
        onChanged={reload}
      />
    </DefaultPageLayout>
  );
};

export default AgentReviewPage;
